// FutureMeetingsTable.tsx
import React, { useState } from "react";
import { ProColumns } from "@ant-design/pro-table/es/typing";
import { Button } from "antd";
import dayjs from "dayjs";
import {
  PlusCircleOutlined,
  LinkOutlined,
  FileOutlined,
  EditOutlined,
} from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import Table from "../../../../../../Common/Table";
import { useNavigate, useParams, useSearchParams } from "react-router";
import {
  MeetingGetParams,
  MeetingTableRow,
  useMeetingTableGet,
} from "../../../../../../../hooks/api/meetings";
import { getSorterParams } from "../../../../../../../utils";
import { SortOrder } from "antd/es/table/interface";
import { RequestData } from "@ant-design/pro-components";
import { IconLink } from "../../../../../../Common/Icon";
import { useVaadTabs } from "../../context";

interface FutureMeetingsTableProps {
  onTotalChange?: (total: number) => void;
}

export const columns: ProColumns<MeetingTableRow>[] = [
  {
    title: "Name",
    dataIndex: "name",
    key: "name",
    hideInSearch: true,
  },
  {
    title: "Date",
    dataIndex: "startTime",
    key: "startTime",
    sorter: true,
    hideInSearch: true,
    render: (_: any, record: MeetingTableRow) => {
      const date = dayjs(record.startTimeLocal);
      return date.format("MMM D, YYYY, ddd");
    },
  },
  {
    title: "Time",
    dataIndex: "startTime",
    key: "startTime",
    hideInSearch: true,
    render: (_: any, record: MeetingTableRow) => {
      const start = dayjs(record.startTimeLocal);
      return `${start.format("h:mm A")} `;
    },
  },
  {
    title: "Location",
    dataIndex: "address",
    key: "address",
    sorter: true,
    hideInSearch: true,
  },
  {
    title: "Middah",
    dataIndex: "middah",
    key: "middah",
    sorter: true,
    valueType: "select",
    render: (_, record: MeetingTableRow) => <span>{record.middah.name}</span>,
  },
  {
    title: "Zoom Link",
    dataIndex: "zoomLink",
    key: "zoomLink",
    hideInSearch: true,
    render: (_, record) => {
      if (!record.zoomLink) return "-";
      return (
        <a
          href={record.zoomLink}
          target="_blank"
          rel="noopener noreferrer"
          style={{
            color: "#1677FF",
            textDecoration: "underline",
            display: "flex",
            alignItems: "center",
            gap: 8,
          }}
        >
          <IconLink />
          Join Meeting
        </a>
      );
    },
  },
  {
    title: "Attachments",
    key: "attachments",
    hideInSearch: true,
    render: (_, record) => {
      const totalCount =
        (record.readings?.length || 0) + (record.recordings?.length || 0);

      if (totalCount === 0) return <span>None</span>;

      return (
        <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <FileOutlined />
          {totalCount} File{totalCount > 1 ? "s" : ""}
        </span>
      );
    },
  },
];

function ToolBar({ onCreate }: { onCreate: () => void }) {
  const navigate = useNavigate();
  return [
    <Button
      key="create"
      icon={<PlusCircleOutlined />}
      type="primary"
      onClick={(e) => {
        e.preventDefault();
        navigate("create");
      }}
    >
      Add Meeting
    </Button>,
  ];
}

export default function FutureMeetingsTable({
  onTotalChange,
}: FutureMeetingsTableProps): React.ReactNode | null {
  const { id } = useParams();
  const navigate = useNavigate();
  const [, setSearchParams] = useSearchParams();
  const [tableKey, setTableKey] = useState<number>(() => Date.now());
  const { t, i18n } = useTranslation();
  const [createOpen, setCreateOpen] = useState(false);
  const meetingTableGet = useMeetingTableGet();
  const { selectedMiddahId, middahOptions, vaadData, setMeetingsSubTab } =
    useVaadTabs();

  const columns: ProColumns<MeetingTableRow>[] = [
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      hideInSearch: true,
    },
    {
      title: "Date",
      dataIndex: "startTime",
      key: "startTime",
      sorter: true,
      hideInSearch: true,
      render: (_: any, record: MeetingTableRow) => {
        const date = dayjs(record.startTimeLocal);
        return date.format("MMM D, YYYY, ddd");
      },
    },
    {
      title: "Time",
      dataIndex: "startTime",
      key: "startTime",
      hideInSearch: true,
      render: (_: any, record: MeetingTableRow) => {
        const start = dayjs(record.startTimeLocal);
        return `${start.format("h:mm A")} `;
      },
    },
    {
      title: "Location",
      dataIndex: "address",
      key: "address",
      sorter: true,
      hideInSearch: true,
    },
    {
      title: "Middah",
      dataIndex: "middahIds",
      key: "middahIds",
      sorter: true,
      valueType: "select",
      valueEnum: Object.fromEntries(
        middahOptions.map((m) => [m.value, { text: m.label }]),
      ),
      render: (_, record: MeetingTableRow) => (
        <span>{record.middah?.name || "-"}</span>
      ),
    },
    {
      title: "Zoom Link",
      dataIndex: "zoomLink",
      key: "zoomLink",
      hideInSearch: true,
      render: (_, record) => {
        const zoomLink = record.zoomLink || vaadData?.zoomLink;
        if (!zoomLink) return "-";
        return (
          <a
            href={zoomLink}
            target="_blank"
            rel="noopener noreferrer"
            style={{
              color: "#1677FF",
              textDecoration: "underline",
              display: "flex",
              alignItems: "center",
              gap: 8,
            }}
          >
            <IconLink />
            Join Meeting
          </a>
        );
      },
    },
    {
      title: "Attachments",
      key: "attachments",
      hideInSearch: true,
      render: (_, record) => {
        const totalCount =
          (record.readings?.length || 0) +
          (record.recordings?.length || 0) +
          (record.charts?.length || 0);

        if (totalCount === 0) return <span>None</span>;

        return (
          <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <FileOutlined />
            {totalCount} File{totalCount > 1 ? "s" : ""}
          </span>
        );
      },
    },
  ];

  const tableRequest = (
    {
      current,
      pageSize,
      tab,
      ...args
    }: Record<string, string> & {
      pageSize?: number;
      current?: number;
      keyword?: string;
      tab?: string;
    },
    sorter: Record<string, SortOrder>,
  ): Promise<Partial<RequestData<MeetingTableRow>>> => {
    const newParams: MeetingGetParams = {
      period: "future",
      vaadId: id,
      page: current || 1,
      orderByColumn: "startTime",
      orderBy: "ASC",
      pageSize: pageSize || 10,
      // middahIds: selectedMiddahId ? [selectedMiddahId] : undefined,
      ...args,
      ...getSorterParams(sorter),
    };

    setSearchParams({ ...args, ...getSorterParams(sorter) }, { replace: true });

    return meetingTableGet.fetch(newParams).then((response) => {
      if (response) {
        const { data, total } = response;
        onTotalChange?.(response.total);

        return {
          data: data || [],
          success: true,
          total,
        };
      }

      return { data: [], success: false, total: 0 };
    });
  };

  return (
    <>
      <Table<MeetingTableRow>
        key={`future-${tableKey}-${i18n.language}-${selectedMiddahId}`} // <-- обновляем при смене мидды
        // Future/Past tables share the same URL; don't sync pagination to it,
        // otherwise a stale current=N leaks across the Segmented sub-tabs.
        form={{ syncToUrl: false }}
        resetKey={() => setTableKey(Date.now())}
        className="tableCustomPadding"
        headerTitle={t("pages.meetings.future")}
        request={tableRequest}
        onReset={() => {
          setSearchParams({}, { replace: true });
          setTableKey(Date.now());
        }}
        toolBarRender={() => [
          <ToolBar key="toolbar" onCreate={() => setCreateOpen(true)} />,
        ]}
        columns={columns}
        actions={[
          [
            "edit",
            ({ record }) => ({
              title: "Edit",
              onClick: (e: MouseEvent) => {
                e.stopPropagation();
                setMeetingsSubTab("future");
                navigate(`${record.id}/edit`, {
                  state: { mode: "edit-future" },
                });
              },
            }),
          ],
        ]}
        search={{
          style: {
            // display: "none",
            paddingBottom: 0,
          },
        }}
        options={{
          search: {
            name: "search",
            placeholder: "Search by meeting name",
            style: { width: 280 },
          },
        }}
        columnsState={{
          persistenceKey: "pro-table-future-meetings",
          persistenceType: "localStorage",
        }}
      />
    </>
  );
}
